import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../App.css";
import logo from "../assests/images/bytethrills.png";

function Header({ scrolled }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(location.pathname);

  useEffect(() => {
    const controlNavbar = () => {
      if (window.scrollY > lastScrollY && window.scrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener("scroll", controlNavbar);
    return () => {
      window.removeEventListener("scroll", controlNavbar);
    };
  }, [lastScrollY]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentRoute(location.pathname);
    setIsMenuOpen(false); // Close mobile menu on route change
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavigation = (url, isExternal = false) => {
    setIsMenuOpen(false);
    if (isExternal) {
      window.location.href = url; // External navigation
    } else {
      navigate(url); // Internal navigation
    }
  };

  const SOLID_HEADER_ROUTES = [
    "/joinus", "/aboutus", "/business", "/contactus",
    "/legal/privacy-policy", "/legal/terms-of-service",
    "/legal/copyright-policy", "/legal/cookie-policy",
    "/legal/safety-center",
    "/privacy", "/privacypolicy", "/terms", "/copyright", "/cookies", "/safety",
    "/community-guidelines", "/contact", "/help",
    "/features", "/blogs"
  ];

  const isTransparent =
    !scrolled &&
    window.scrollY <= 750 &&
    !SOLID_HEADER_ROUTES.includes(currentRoute);

  return (
    <header
      className={`transition-all duration-300 fixed top-0 left-0 right-0 z-[1000] text-white font-inter
        ${
          isTransparent
            ? "bg-transparent"
            : "bg-[#000000] bg-opacity-90 backdrop-filter backdrop-blur-lg"
        }
        ${isVisible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"}`}
    >
      <div className="max-w-[1440px] mx-auto flex justify-between items-center py-2  px-4 md:px-8">
        <div
          className="flex items-center cursor-pointer"
          onClick={() => handleNavigation("/")}
        >
          <img
            src={logo}
            alt="ByteThrills Logo"
            className="object-contain w-12 h-12 transition-transform duration-200 md:w-14 md:h-14 hover:scale-105"
          />
          <div className="ml-3 text-2xl font-bold tracking-tight md:text-3xl">
            ByteThrills
          </div>
        </div>

        {/* Menu Icon (visible only on smaller screens) */}
        <div
          className="md:hidden cursor-pointer p-3 rounded-md hover:bg-[#00d17e] hover:bg-opacity-20 transition-colors duration-200"
          onClick={toggleMenu}
        >
          {isMenuOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          )}
        </div>

        {/* Navigation for larger screens */}
        <nav className="items-center hidden space-x-8 font-semibold md:flex">
          <div
            onClick={() => handleNavigation("/")}
            className="relative text-base cursor-pointer group"
          >
            <span className="transition-colors duration-200 md:text-lg group-hover:text-[#00d17e]">
              About Us
            </span>
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00d17e] transition-all duration-200 group-hover:w-full"></span>
          </div>
          <div
            onClick={() => handleNavigation("https://partner.bytethrills.com", true)}
            className="relative text-base cursor-pointer group"
          >
            <span className="transition-colors duration-200 md:text-lg group-hover:text-[#00d17e]">
              Partners
            </span>
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00d17e] transition-all duration-200 group-hover:w-full"></span>
          </div>
          <div
            onClick={() => handleNavigation("https://business.bytethrills.com/", true)}
            className="relative text-base cursor-pointer group"
          >
            <span className="transition-colors duration-200 md:text-lg group-hover:text-[#00d17e]">
              Business
            </span>
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00d17e] transition-all duration-200 group-hover:w-full"></span>
          </div>
          <div
            onClick={() => handleNavigation("https://www.bytehearts.com/careers", true)}
            className="relative text-base cursor-pointer group"
          >
            <span className="transition-colors duration-200 md:text-lg group-hover:text-[#00d17e]">
              Careers
            </span>
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00d17e] transition-all duration-200 group-hover:w-full"></span>
          </div>
          <button
            onClick={() => handleNavigation("https://www.bytehearts.com/contact", true)}
            className={`text-base px-5 py-2 rounded-lg font-semibold transition-all duration-200 shadow-md
              ${
                isTransparent
                  ? "border-2 border-gray-200 text-white hover:bg-[#00d17e] hover:border-[#00d17e] hover:shadow-lg"
                  : "border-2 border-[#000] bg-[#000] text-[#FAFAFA] hover:bg-[#00d17e] hover:text-[#000] hover:shadow-lg"
              }`}
          >
            Contact Us
          </button>
        </nav>
      </div>

      {/* Navigation Menu for smaller screens */}
      {isMenuOpen && (
        <div
          className={`absolute w-full top-0 left-0 z-50 md:hidden transition-all duration-500 transform bg-[#EE1D52] bg-opacity-95 backdrop-filter backdrop-blur-lg min-h-screen
            ${isMenuOpen ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"}`}
        >
          <div className="flex flex-col items-center justify-center min-h-screen gap-10 text-white">
            <div
              onClick={toggleMenu}
              className="absolute top-5 right-5 cursor-pointer hover:text-[#00d17e] transition-colors duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <div
              onClick={() => handleNavigation("/")}
              className="text-xl font-semibold cursor-pointer hover:text-[#00d17e] transition-colors duration-200 py-2"
            >
              About Us
            </div>
            <div
              onClick={() => handleNavigation("https://partner.bytethrills.com", true)}
              className="text-xl font-semibold cursor-pointer hover:text-[#00d17e] transition-colors duration-200 py-2"
            >
              Partners
            </div>
            <div
              onClick={() => handleNavigation("https://business.bytethrills.com/", true)}
              className="text-xl font-semibold cursor-pointer hover:text-[#00d17e] transition-colors duration-200 py-2"
            >
              Business
            </div>
            <div
              onClick={() => handleNavigation("https://www.bytehearts.com/careers", true)}
              className="text-xl font-semibold cursor-pointer hover:text-[#00d17e] transition-colors duration-200 py-2"
            >
              Careers
            </div>
            <div
              onClick={() => handleNavigation("https://www.bytehearts.com/contact", true)}
              className="text-xl font-semibold cursor-pointer hover:text-[#00d17e] transition-colors duration-200 py-2"
            >
              Contact Us
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;